import { locale } from '../lib/locale';

/**
 * Settings (1.19): sections, AI helper, family and people, data, wall display and PIN, this
 * device, display, reminders, tasks and rewards, timetables, shopping. German is the master.
 * The calendar section has its own dictionary (calendarSettings.ts).
 */
export const SETTINGS_TEXT = {
  de: {
    sections: {
      ki: { title: 'KI-Helfer', hint: 'Beta · Einfach sagen, Fotos verstehen' },
      familie: { title: 'Familie', hint: 'Name, Wohnort, Personen, Einladen, Daten' },
      aufgaben: { title: 'Aufgaben', hint: 'Routinen, geteilte Aufgaben, Sterne' },
      belohnungen: { title: 'Belohnungen & Ziel', hint: 'Was es für Sterne gibt' },
      kalender: {
        title: 'Kalender & Müllabfuhr',
        hint: 'Google, Outlook, iCloud, Schule, Abfuhrtermine',
      },
      erinnerungen: { title: 'Erinnerungen', hint: 'Wann und wen die App erinnert' },
      einkauf: { title: 'Einkaufsliste', hint: 'Eigene Liste oder Bring!' },
      stundenplan: {
        title: 'Schicht- und Stundenpläne',
        hint: 'Schichten im Rhythmus, Schul- und Kitazeiten',
      },
      geraete: { title: 'Familienmodus & PIN', hint: 'Gemeinsames Gerät koppeln, Eltern-PIN' },
      'dieses-geraet': {
        title: 'Dieses Gerät',
        hint: 'Nachrichten aufs Handy, Gong am Wanddisplay',
      },
      anzeige: { title: 'Anzeige', hint: 'Größe, Tageslicht-Farben, Ruhebild, Festtage' },
    },
    parentsOnly: 'Einstellungen sind für Eltern.',
    openWithPin: 'Mit Eltern-PIN öffnen',
    whatsNew: (version: string) => `Was ist neu · Version ${version}`,
    signOut: 'Abmelden',
    nav: 'Einstellungen',
    saveError: 'Konnte nicht gespeichert werden. Bitte später nochmal versuchen.',
    on: 'An',
    off: 'Aus',
    save: 'Speichern',
    saved: 'Gespeichert',
    cancel: 'Abbrechen',
    failed: 'Das hat nicht geklappt. Bitte nochmal versuchen.',
    minutes: (n: string) => `${n} Min.`,
    clock: (h: string) => `${h} Uhr`,
    // AI helper
    aiTitle: 'Sag es einfach – die App trägt es für euch ein.',
    aiBenefits: [
      {
        title: 'Einfach sagen statt tippen',
        text: '„Lotta hat nächsten Dienstag um 15 Uhr Zahnarzt, Impfpass mitnehmen“ – und der Termin steht fertig da, mit Person, Uhrzeit und Mitnehmen-Liste.',
      },
      {
        title: 'Papierkalender abfotografieren',
        text: 'Alle Termine auf einmal übernehmen. Was schon im Kalender steht, erkennt die App und lässt es weg.',
      },
      {
        title: 'Stundenplan in Sekunden',
        text: 'Foto vom Stundenplan – Schulzeiten und Fächer sind eingetragen, ohne eine Zeile zu tippen.',
      },
      {
        title: 'Einladungen und Zettel aus der Schule',
        text: 'Kindergeburtstag, Elternabend, Ausflug: fotografieren, prüfen, fertig.',
      },
      {
        title: 'Ändern und löschen',
        text: '„Fußball ist heute erst um 18 Uhr“ oder „Lösch den Zahnarzt am Dienstag“ – die App findet den Termin selbst, ihr bestätigt.',
      },
      {
        title: 'Aufgaben mit Sternen',
        text: '„Jonas bringt samstags den Müll raus, 5 Sterne“ – fertig ist die neue Aufgabe für die Kinder.',
      },
      {
        title: 'Mehrere Dinge in einem Satz',
        text: '„Wir brauchen Milch und Eier, und Oma kommt Sonntag“ – Einkaufsliste und Kalender in einem Rutsch.',
      },
      {
        title: 'Ihr behaltet die Kontrolle',
        text: 'Die KI macht nur Vorschläge. Eingetragen wird erst, wenn ihr „Übernehmen“ tippt.',
      },
    ],
    aiHow:
      'So geht’s: „＋“ → „Einfach sagen“. Der KI-Helfer ist noch eine Beta und für euch kostenlos, solange sie läuft.',
    aiLabel: 'KI-Helfer',
    aiHint:
      'Gilt für die ganze Familie. Nutzen können ihn nur Mama und Papa, am Wanddisplay mit der Eltern-PIN. Ausschalten geht jederzeit.',
    goodToKnow: 'Gut zu wissen',
    aiData:
      'Was ihr bei „Einfach sagen“ schreibt, sprecht oder fotografiert, wird zur Auswertung an Google Gemini geschickt – dazu nur eure Vornamen und das heutige Datum. Euer Kalender, die Sterne und alles andere in der App bleiben bei euch. Die KI bekommt nur etwas zu sehen, wenn ihr „Einfach sagen“ benutzt.',
    aiGoogle:
      'Die Auswertung läuft bei Google Cloud in Frankfurt. Google nutzt diese Inhalte nicht zum Trainieren seiner KI und speichert sie nicht dauerhaft.',
    aiAdvice:
      'Unsere Empfehlung: Gebt keine sensiblen Informationen ein – zum Beispiel Gesundheitsdetails, Adressen, Konto- oder Ausweisnummern und Passwörter.',
    aiEnough: '„Kinderarzt Dienstag 10:30“ reicht völlig.',
    aiSpeech:
      'Sprechen nutzt die Spracherkennung eures Browsers (bei Chrome von Google, bei Safari von Apple).',
    // family
    familyName: 'Name der Familie',
    place: 'Wohnort (für Wetter, Tageslicht und Ferien)',
    placeHint: 'Nur der Ort, keine Adresse.',
    placeSuggestions: 'Vorschläge',
    ownAccount: ' · eigenes Konto',
    addPerson: 'Person hinzufügen',
    newPerson: 'Neue Person',
    name: 'Name',
    whoIsThis: 'Wer ist das?',
    color: 'Farbe',
    taken: 'vergeben',
    birthday: 'Geburtstag (optional)',
    birthdayHint:
      'Nur Tag und Monat, z. B. 14.11. – für die Geburtstags-Ansicht und die Erinnerung.',
    birthdayPlaceholder: 'TT.MM.',
    birthdayError: 'Bitte so eintragen: 14.11.',
    routinesHint: 'Bekommt passende Routinen fürs Alter – änderbar unter Aufgaben.',
    accountFor: (name: string) => `Eigenes Konto für ${name}`,
    inviteCode: 'Einladungscode:',
    inviteValid: (name: string) => `7 Tage gültig. ${name} meldet sich an und gibt den Code ein.`,
    createInvite: 'Einladungscode erstellen',
    reallyRemove: 'Wirklich entfernen',
    remove: 'Entfernen',
    add: 'Hinzufügen',
    letter: 'Buchstabe',
    emoji: 'Emoji statt Buchstabe',
    emojiHint: 'Kinder suchen sich gern ein Tier aus.',
    otherEmoji: 'Oder ein anderes Emoji',
    kinds: {
      parent: { label: 'Elternteil', description: 'verwaltet alles' },
      teen: { label: 'Teen', description: 'ab ca. 13, eigenes Handy' },
      kid: { label: 'Kind', description: 'liest schon, ca. 8–12' },
      little: { label: 'Kleines Kind', description: 'liest noch nicht' },
    },
    colors: {
      clay: 'Koralle',
      sun: 'Messing',
      pine: 'Tanne',
      lagoon: 'Fjord',
      berry: 'Preiselbeere',
      plum: 'Heide',
      meadow: 'Moos',
      stone: 'Stein',
    },
    // data
    yourData: 'Eure Daten',
    dataText:
      'Alles, was ihr hier gespeichert habt – Personen, Aufgaben, Sterne, Termine, Notizen – als eine Datei zum Aufheben. PIN und geheime Kalender-Links sind nicht dabei.',
    collecting: 'Wird zusammengestellt …',
    download: 'Alle Daten herunterladen',
    downloaded: 'Heruntergeladen',
    startOver: 'Neu anfangen',
    demoFamily: 'Beispielfamilie',
    family: 'Familie',
    demoDelete: 'Löscht alle Beispieldaten. Danach legst du eure eigene Familie an.',
    familyDelete:
      'Löscht alles von eurer Familie endgültig: Personen, Aufgaben, Sterne, Termine. Eingeladene Konten und Wanddisplays werden abgemeldet.',
    reallyDelete: (what: string) => `${what} wirklich löschen`,
    delete: (what: string) => `${what} löschen`,
    // wall display and PIN
    wall: 'Wanddisplay',
    pairedOn: (date: string) => `gekoppelt am ${date}`,
    unpair: (name: string) => `${name} entkoppeln`,
    lastSeen: (when: string) => `zuletzt aktiv: ${when}`,
    todayWord: 'heute',
    quietSince: (date: string) => `seit ${date} nicht mehr aktiv – entfernen?`,
    deviceName: 'Name des Geräts',
    namePlaceholder: 'z. B. Küche, Flur, Wohnzimmer',
    saveName: 'Name speichern',
    rename: (name: string) => `${name} umbenennen`,
    unpairHint: 'Das Gerät zeigt danach wieder die Anmeldung.',
    unpairConfirm: 'Entkoppeln',
    openOnTablet: (url: string) => `Auf dem Tablet ${url} öffnen und eingeben:`,
    scanOrType: (url: string) =>
      `Mit der Kamera des Tablets scannen – oder dort ${url} öffnen und den Code eintippen:`,
    qrLabel: 'QR-Code zum Koppeln',
    validFor: (time: string) => `noch ${time} Min. gültig`,
    pairWall: 'Gerät koppeln',
    familyMode: {
      title: 'Familienmodus oder Anmeldung?',
      intro:
        'Auf dem eigenen Handy meldet ihr euch an. Ein Gerät, das alle nutzen – in der Küche, im Flur oder an der Wand –, koppelt ihr. Nur gekoppelt:',
      points: [
        'bleibt der Bildschirm an und wird nachts ruhig',
        'kommt nach einer Weile das Ruhebild',
        'erscheint 15 Minuten vor Terminen eine große Erinnerung mit Gong',
        'springt es nach kurzer Zeit zurück zu „Heute“ für alle',
        'ändern Kinder keine Termine, Aufgaben oder Einstellungen',
        'öffnen Eltern ihren Bereich mit der PIN – er schließt sich nach 2 Minuten wieder',
      ],
      hintTitle: 'Ein Gerät für alle?',
      hintText:
        'Das Tablet in der Küche, im Flur oder an der Wand koppelt ihr im Familienmodus – dann bleibt es an, zeigt das Ruhebild und erinnert mit Gong.',
      hintLink: 'Zu „Familienmodus & PIN“',
      thisPaired: (name: string) => `Dieses Gerät ist im Familienmodus gekoppelt: ${name}.`,
      thisSignedIn: 'Dieses Gerät ist angemeldet – nicht im Familienmodus.',
    },
    creatingCode: 'Code wird erzeugt …',
    pin: 'Eltern-PIN',
    pinHint:
      'Öffnet am Wanddisplay den Elternmodus – Freigaben, Sterne, Einstellungen. Er endet 2 Minuten nach der letzten Berührung.',
    changePin: 'PIN ändern',
    pinSaved: 'Neue PIN gespeichert',
    newPin4: 'Neue PIN (4 Ziffern)',
    repeatPin: 'Nochmal zur Bestätigung',
    newPin: 'Neue PIN',
    pinAgain: 'PIN wiederholen',
    pinsDiffer: 'Die PINs sind verschieden – nochmal von vorn.',
    // this device
    pushOnDevice: 'Erinnerungen auf diesem Gerät',
    push: {
      on: 'Dieses Gerät bekommt Erinnerungen.',
      off: 'Dieses Gerät bekommt noch keine Erinnerungen.',
      denied:
        'Nachrichten sind für diese App im Browser bzw. Handy gesperrt. In den Einstellungen des Geräts „Mitteilungen“ für Larovi Home erlauben.',
      unsupported: 'Dieser Browser kann leider keine Nachrichten empfangen.',
      install:
        'Auf dem iPhone gehen Nachrichten, wenn Larovi Home als App installiert ist: im Safari auf „Teilen“ tippen, dann „Zum Home-Bildschirm“ – und die App von dort öffnen.',
    },
    switchOff: 'Ausschalten',
    switchOn: 'Einschalten',
    gong: 'Gong bei Erinnerungen (am Wanddisplay)',
    gongHint: 'Nur auf diesem Gerät. Erklingt kurz vor einem Termin – nie in der Nachtruhe.',
    gongLevels: { off: 'Aus', soft: 'Leise', medium: 'Mittel', loud: 'Laut' },
    listen: 'Probehören',
    // display
    size: 'Größe auf diesem Gerät',
    sizeHint:
      'Macht Schrift und Knöpfe kleiner oder größer – nur hier, andere Geräte bleiben, wie sie sind.',
    fullscreen: 'Vollbild auf diesem Gerät',
    fullscreenHint:
      'Blendet die Leisten oben und unten aus – schön fürs Wanddisplay und das Ruhebild. Startet beim nächsten Antippen; zum Verlassen vom Rand wischen.',
    daylight: 'Tageslicht-Farben',
    daylightHint:
      'Die Farben folgen der Sonne über eurem Wohnort – morgens frisch, abends warm, nachts dunkel.',
    strong: 'Kräftig',
    subtle: 'Dezent',
    daylightAll:
      'Gilt für alle Geräte der Familie. Bei „Aus“ richtet sich hell oder dunkel nach dem Gerät.',
    rest: 'Ruhebild am Wanddisplay',
    restHint:
      'Wenn niemand das Wanddisplay benutzt, zeigt es Uhr, Wetter, den nächsten Termin und die Pinnwand vor einem ruhigen Himmel. Antippen – und alles ist wieder da.',
    restAfter: 'Ruhebild nach',
    nightFrom: 'Nachts fast schwarz ab',
    nightHint: 'Schont Augen und Bildschirm – nur eine schwache Uhr bleibt.',
    never: 'Nie',
    until: 'bis',
    showRest: 'Ruhebild jetzt ansehen',
    festive: 'Festtage und Geburtstage',
    festiveHint:
      'Advent, Weihnachten, Silvester, Ostern und eure Geburtstage bekommen ein eigenes Ruhebild und einen Gruß auf „Heute“.',
    whichFestivals: 'Welche Feste?',
    // reminders
    remindersIntro:
      'Gilt für die ganze Familie: Beide Eltern werden erinnert, die Großen zusätzlich bei ihren eigenen Terminen. Am einzelnen Termin lässt sich das unter „Erinnern“ ändern.',
    timedEvents: 'Termine mit Uhrzeit',
    before10: '10 Min. vorher',
    before30: '30 Min. vorher',
    before60: '1 Std. vorher',
    eve: 'Ganztägige Termine und Müll – am Vorabend um',
    bring: 'Mitnehmen – 2 Std. vorher',
    bringHint: '„Trinkflasche und Badesachen einpacken“ bei Terminen mit Mitnehmen-Liste.',
    approvals: 'Freigaben und Belohnungswünsche – sofort',
    birthdays: 'Geburtstage – eine Woche vorher und am Tag',
    birthdaysHint: 'Die Geburtstage tragt ihr unter Familie bei jeder Person ein.',
    quietFrom: 'Ruhezeit von',
    quietHint: 'In der Ruhezeit kommen keine Nachrichten – auch keine Freigaben.',
    // tasks and rewards
    dayparts: {
      morning: 'morgens',
      afternoon: 'nachmittags',
      evening: 'abends',
      anytime: 'egal wann',
    },
    sharedFirst: 'Geteilt – wer zuerst kommt',
    forAll: 'Für alle',
    withApproval: ' · mit Freigabe',
    edit: (title: string) => `${title} bearbeiten`,
    deleteItem: (title: string) => `${title} löschen`,
    noTasks: 'Noch keine Aufgaben.',
    addTask: 'Aufgabe anlegen',
    newTask: 'Neue Aufgabe',
    editTask: 'Aufgabe bearbeiten',
    stars: 'Sterne',
    forAllKids: 'für alle Kinder',
    addReward: 'Belohnung anlegen',
    goalTitle: 'Familienziel – alle sammeln gemeinsam',
    goal: 'Ziel',
    goalDefault: 'Familienkino',
    saveGoal: 'Ziel speichern',
    newReward: 'Neue Belohnung',
    editReward: 'Belohnung bearbeiten',
    // timetables
    forKids: 'Für Kinder und Teens – leg zuerst eine Person an.',
    noPlan: 'Noch kein Plan',
    change: 'Ändern',
    create: 'Anlegen',
    timetableOf: (name: string) => `Stundenplan ${name}`,
    shiftPlans: 'Schichtpläne',
    timetables: 'Stundenpläne',
    noShiftPlan: 'Noch kein Schichtplan.',
    newPlan: 'Neuer Plan',
    shiftPlanOf: (name: string) => `Schichtplan ${name}`,
    planSince: (day: string) => `seit ${day}`,
    planUntil: (day: string) => `bis ${day}`,
    what: 'Was?',
    whichDays: 'An welchen Tagen?',
    start: 'Beginn',
    end: 'Ende',
    lessons: 'Fächer (optional, mit Komma)',
    lessonsExample: 'Deutsch, Mathe, Sport',
    sameTimes: 'Zeiten für alle Tage übernehmen',
    preview: 'Vorschau',
    deletePlan: 'Plan löschen',
    // shopping
    bringAuth: 'Bring! möchte eine neue Anmeldung. Bitte einmal neu verbinden.',
    ownList:
      'Eure Einkaufsliste ist in Larovi: unter „Essen“, auf jedem Handy und am Wanddisplay – im Laden auch ohne Netz.',
    bringInstead: 'Ihr nutzt schon Bring!?',
    bringIntro:
      'Dann verbindet es hier: Larovi zeigt eure Bring!-Liste, und die Larovi-Liste ruht so lange. Beim Einkaufen könnt ihr weiter die Bring!-App nehmen.',
    takeOver: 'Einträge von Bring! in die Larovi-Liste übernehmen',
    bringGoogle: 'Bei Bring! mit Google oder Apple angemeldet?',
    bringPassword1:
      'Dann einmal ein Bring!-Passwort festlegen – eure Liste bleibt, wie sie ist: in der Bring!-App unter',
    bringPath: 'Profil → Weitere Einstellungen → Passwort ändern',
    bringOr: 'oder auf',
    bringPassword2:
      '. Danach hier mit der E-Mail-Adresse eures Google-Kontos und dem neuen Passwort verbinden.',
    bringPrivacy:
      'Das Passwort wird nur zum Anmelden an Bring! geschickt und nicht gespeichert. Die Einkaufsliste wird mit Bring! abgeglichen. Bring! bietet dafür keine offizielle Schnittstelle an – ändert Bring! etwas, kann die Verbindung ausfallen.',
    connected: (list: string, n: number) =>
      `Mit Bring! verbunden – Liste „${list}“, ${n} ${n === 1 ? 'Sache' : 'Sachen'} darauf.`,
    whichList: 'Welche Liste?',
    listChanged: 'Liste gewechselt',
    synced: 'Abgeglichen',
    syncNow: 'Jetzt abgleichen',
    disconnected: 'Bring! ist getrennt – ab jetzt gilt die Larovi-Liste.',
    disconnect: 'Trennen',
    bringEmail: 'E-Mail bei Bring!',
    bringPw: 'Passwort bei Bring!',
    connecting: 'Verbinde …',
    connect: 'Mit Bring! verbinden',
  },
  en: {
    sections: {
      ki: { title: 'AI helper', hint: 'Beta · Just say it, understands photos' },
      familie: { title: 'Family', hint: 'Name, town, people, invites, data' },
      aufgaben: { title: 'Tasks', hint: 'Routines, shared tasks, stars' },
      belohnungen: { title: 'Rewards & goal', hint: 'What stars can buy' },
      kalender: {
        title: 'Calendar & bin collection',
        hint: 'Google, Outlook, iCloud, school, collection days',
      },
      erinnerungen: { title: 'Reminders', hint: 'When and whom the app reminds' },
      einkauf: { title: 'Shopping list', hint: 'Own list or Bring!' },
      stundenplan: {
        title: 'Shift plans and timetables',
        hint: 'Shifts in a rhythm, school and nursery times',
      },
      geraete: { title: 'Family mode & PIN', hint: 'Pair a shared device, parent PIN' },
      'dieses-geraet': {
        title: 'This device',
        hint: 'Messages on your phone, chime on the wall display',
      },
      anzeige: { title: 'Display', hint: 'Size, daylight colours, rest screen, festive days' },
    },
    parentsOnly: 'Settings are for parents.',
    openWithPin: 'Open with the parent PIN',
    whatsNew: (version: string) => `What’s new · Version ${version}`,
    signOut: 'Sign out',
    nav: 'Settings',
    saveError: 'Couldn’t be saved. Please try again later.',
    on: 'On',
    off: 'Off',
    save: 'Save',
    saved: 'Saved',
    cancel: 'Cancel',
    failed: 'That didn’t work. Please try again.',
    minutes: (n: string) => `${n} min`,
    clock: (h: string) => `${h}:00`,
    aiTitle: 'Just say it – the app adds it for you.',
    aiBenefits: [
      {
        title: 'Just say it instead of typing',
        text: '“Lotta has the dentist next Tuesday at 3 pm, bring the vaccination card” – and the event is ready, with person, time and things to bring.',
      },
      {
        title: 'Take a photo of a paper calendar',
        text: 'Take over all events at once. The app spots what’s already in the calendar and leaves it out.',
      },
      {
        title: 'A timetable in seconds',
        text: 'A photo of the timetable – school times and subjects are in, without typing a line.',
      },
      {
        title: 'Invitations and letters from school',
        text: 'Kids’ birthday party, parents’ evening, school trip: take a photo, check, done.',
      },
      {
        title: 'Change and delete',
        text: '“Football is at 6 pm today” or “Delete the dentist on Tuesday” – the app finds the event itself, you confirm.',
      },
      {
        title: 'Tasks with stars',
        text: '“Jonas takes the bins out on Saturdays, 5 stars” – and the new task for the kids is ready.',
      },
      {
        title: 'Several things in one sentence',
        text: '“We need milk and eggs, and Grandma’s coming on Sunday” – shopping list and calendar in one go.',
      },
      {
        title: 'You stay in control',
        text: 'The AI only makes suggestions. Nothing is added until you tap “Add”.',
      },
    ],
    aiHow:
      'How it works: “＋” → “Just say it”. The AI helper is still a beta and free for you while it runs.',
    aiLabel: 'AI helper',
    aiHint:
      'Applies to the whole family. Only Mum and Dad can use it, on the wall display with the parent PIN. You can turn it off any time.',
    goodToKnow: 'Good to know',
    aiData:
      'What you type, say or photograph in “Just say it” is sent to Google Gemini to be understood – along with only your first names and today’s date. Your calendar, the stars and everything else in the app stay with you. The AI only sees something when you use “Just say it”.',
    aiGoogle:
      'It is processed by Google Cloud in Frankfurt. Google doesn’t use this content to train its AI and doesn’t store it permanently.',
    aiAdvice:
      'Our advice: don’t enter sensitive information – for example health details, addresses, bank or ID numbers and passwords.',
    aiEnough: '“Doctor Tuesday 10:30” is all it takes.',
    aiSpeech:
      'Speaking uses your browser’s speech recognition (Google’s in Chrome, Apple’s in Safari).',
    familyName: 'Family name',
    place: 'Town (for weather, daylight and school holidays)',
    placeHint: 'Just the town, no address.',
    placeSuggestions: 'Suggestions',
    ownAccount: ' · own account',
    addPerson: 'Add person',
    newPerson: 'New person',
    name: 'Name',
    whoIsThis: 'Who is this?',
    color: 'Colour',
    taken: 'taken',
    birthday: 'Birthday (optional)',
    birthdayHint: 'Just day and month, e.g. 14/11 – for the birthday view and the reminder.',
    birthdayPlaceholder: 'DD/MM',
    birthdayError: 'Please enter it like this: 14/11',
    routinesHint: 'Gets routines that suit their age – you can change them under Tasks.',
    accountFor: (name: string) => `${name}’s own account`,
    inviteCode: 'Invitation code:',
    inviteValid: (name: string) => `Valid for 7 days. ${name} signs up and enters the code.`,
    createInvite: 'Create invitation code',
    reallyRemove: 'Remove for good',
    remove: 'Remove',
    add: 'Add',
    letter: 'Letter',
    emoji: 'Emoji instead of a letter',
    emojiHint: 'Kids love picking an animal.',
    otherEmoji: 'Or another emoji',
    kinds: {
      parent: { label: 'Parent', description: 'manages everything' },
      teen: { label: 'Teen', description: 'about 13 and up, own phone' },
      kid: { label: 'Kid', description: 'can read, about 8–12' },
      little: { label: 'Little one', description: 'can’t read yet' },
    },
    colors: {
      clay: 'Coral',
      sun: 'Brass',
      pine: 'Pine',
      lagoon: 'Fjord',
      berry: 'Lingonberry',
      plum: 'Heather',
      meadow: 'Moss',
      stone: 'Stone',
    },
    yourData: 'Your data',
    dataText:
      'Everything you’ve saved here – people, tasks, stars, events, notes – as one file to keep. The PIN and secret calendar links aren’t included.',
    collecting: 'Putting it together …',
    download: 'Download all data',
    downloaded: 'Downloaded',
    startOver: 'Start over',
    demoFamily: 'Example family',
    family: 'Family',
    demoDelete: 'Deletes all example data. Then you set up your own family.',
    familyDelete:
      'Deletes everything of your family for good: people, tasks, stars, events. Invited accounts and wall displays are signed out.',
    reallyDelete: (what: string) => `Delete ${what.toLowerCase()} for good`,
    delete: (what: string) => `Delete ${what.toLowerCase()}`,
    wall: 'Wall display',
    pairedOn: (date: string) => `paired on ${date}`,
    unpair: (name: string) => `Unpair ${name}`,
    lastSeen: (when: string) => `last in use: ${when}`,
    todayWord: 'today',
    quietSince: (date: string) => `not in use since ${date} – remove?`,
    deviceName: 'Device name',
    namePlaceholder: 'e.g. kitchen, hall, living room',
    saveName: 'Save name',
    rename: (name: string) => `Rename ${name}`,
    unpairHint: 'The device will show the sign-in again.',
    unpairConfirm: 'Unpair',
    openOnTablet: (url: string) => `On the tablet, open ${url} and enter:`,
    scanOrType: (url: string) =>
      `Scan with the tablet’s camera – or open ${url} there and type in the code:`,
    qrLabel: 'QR code for pairing',
    validFor: (time: string) => `valid for ${time} min`,
    pairWall: 'Pair device',
    familyMode: {
      title: 'Family mode or signing in?',
      intro:
        'On your own phone, you sign in. A device everyone uses – in the kitchen, the hall or on the wall – you pair. Only when paired:',
      points: [
        'the screen stays on and goes quiet at night',
        'the rest screen appears after a while',
        'a large reminder with a chime appears 15 minutes before events',
        'it goes back to “Today” for everyone after a short while',
        'kids can’t change events, tasks or settings',
        'parents open their area with the PIN – it closes again after 2 minutes',
      ],
      hintTitle: 'One device for everyone?',
      hintText:
        'Pair the tablet in the kitchen, the hall or on the wall in family mode – it then stays on, shows the rest screen and reminds you with a chime.',
      hintLink: 'Go to “Family mode & PIN”',
      thisPaired: (name: string) => `This device is paired in family mode: ${name}.`,
      thisSignedIn: 'This device is signed in – not in family mode.',
    },
    creatingCode: 'Creating the code …',
    pin: 'Parent PIN',
    pinHint:
      'Opens parent mode on the wall display – approvals, stars, settings. It ends 2 minutes after the last touch.',
    changePin: 'Change PIN',
    pinSaved: 'New PIN saved',
    newPin4: 'New PIN (4 digits)',
    repeatPin: 'Once more to confirm',
    newPin: 'New PIN',
    pinAgain: 'Repeat PIN',
    pinsDiffer: 'The PINs don’t match – start again.',
    pushOnDevice: 'Reminders on this device',
    push: {
      on: 'This device gets reminders.',
      off: 'This device doesn’t get reminders yet.',
      denied:
        'Messages are blocked for this app in the browser or on the phone. Allow “Notifications” for Larovi Home in the device settings.',
      unsupported: 'Sorry, this browser can’t receive messages.',
      install:
        'On an iPhone, messages work once Larovi Home is installed as an app: in Safari tap “Share”, then “Add to Home Screen” – and open the app from there.',
    },
    switchOff: 'Turn off',
    switchOn: 'Turn on',
    gong: 'Chime for reminders (on the wall display)',
    gongHint: 'Only on this device. Sounds shortly before an event – never during night rest.',
    gongLevels: { off: 'Off', soft: 'Soft', medium: 'Medium', loud: 'Loud' },
    listen: 'Listen',
    size: 'Size on this device',
    sizeHint:
      'Makes text and buttons smaller or larger – only here, other devices stay as they are.',
    fullscreen: 'Full screen on this device',
    fullscreenHint:
      'Hides the bars at the top and bottom – nice for the wall display and the rest screen. Starts with the next tap; swipe in from the edge to leave it.',
    daylight: 'Daylight colours',
    daylightHint:
      'The colours follow the sun over your town – fresh in the morning, warm in the evening, dark at night.',
    strong: 'Strong',
    subtle: 'Subtle',
    daylightAll:
      'Applies to all the family’s devices. With “Off”, light or dark follows the device.',
    rest: 'Rest screen on the wall display',
    restHint:
      'When nobody is using the wall display, it shows the clock, weather, the next event and the pinboard against a calm sky. Tap it – and everything is back.',
    restAfter: 'Rest screen after',
    nightFrom: 'Almost black at night from',
    nightHint: 'Easy on eyes and screen – only a faint clock stays.',
    never: 'Never',
    until: 'until',
    showRest: 'See the rest screen now',
    festive: 'Festive days and birthdays',
    festiveHint:
      'Advent, Christmas, New Year’s Eve, Easter and your birthdays get their own rest screen and a greeting on “Today”.',
    whichFestivals: 'Which festive days?',
    remindersIntro:
      'Applies to the whole family: both parents are reminded, the older kids also for their own events. You can change it for a single event under “Remind”.',
    timedEvents: 'Events with a time',
    before10: '10 min before',
    before30: '30 min before',
    before60: '1 hr before',
    eve: 'All-day events and bins – the evening before at',
    bring: 'Things to bring – 2 hrs before',
    bringHint: '“Pack water bottle and swimming things” for events with a list of things to bring.',
    approvals: 'Approvals and reward requests – straight away',
    birthdays: 'Birthdays – a week before and on the day',
    birthdaysHint: 'Add birthdays for each person under Family.',
    quietFrom: 'Quiet time from',
    quietHint: 'No messages arrive during quiet time – not even approvals.',
    dayparts: {
      morning: 'mornings',
      afternoon: 'afternoons',
      evening: 'evenings',
      anytime: 'any time',
    },
    sharedFirst: 'Shared – first come, first served',
    forAll: 'For everyone',
    withApproval: ' · needs approval',
    edit: (title: string) => `Edit ${title}`,
    deleteItem: (title: string) => `Delete ${title}`,
    noTasks: 'No tasks yet.',
    addTask: 'Add task',
    newTask: 'New task',
    editTask: 'Edit task',
    stars: 'stars',
    forAllKids: 'for all kids',
    addReward: 'Add reward',
    goalTitle: 'Family goal – everyone collects together',
    goal: 'Goal',
    goalDefault: 'Family film night',
    saveGoal: 'Save goal',
    newReward: 'New reward',
    editReward: 'Edit reward',
    forKids: 'For kids and teens – add a person first.',
    noPlan: 'No plan yet',
    change: 'Change',
    create: 'Create',
    timetableOf: (name: string) => `${name}’s timetable`,
    shiftPlans: 'Shift plans',
    timetables: 'Timetables',
    noShiftPlan: 'No shift plan yet.',
    newPlan: 'New plan',
    shiftPlanOf: (name: string) => `${name}’s shift plan`,
    planSince: (day: string) => `since ${day}`,
    planUntil: (day: string) => `until ${day}`,
    what: 'What?',
    whichDays: 'Which days?',
    start: 'Start',
    end: 'End',
    lessons: 'Subjects (optional, comma-separated)',
    lessonsExample: 'English, maths, PE',
    sameTimes: 'Use these times for every day',
    preview: 'Preview',
    deletePlan: 'Delete plan',
    bringAuth: 'Bring! wants you to sign in again. Please reconnect once.',
    ownList:
      'Your shopping list is in Larovi: under “Food”, on every phone and on the wall display – in the shop even without signal.',
    bringInstead: 'Already using Bring!?',
    bringIntro:
      'Then connect it here: Larovi shows your Bring! list, and the Larovi list rests while it’s connected. You can keep using the Bring! app when you shop.',
    takeOver: 'Move the items from Bring! to the Larovi list',
    bringGoogle: 'Signed in to Bring! with Google or Apple?',
    bringPassword1:
      'Then set a Bring! password once – your list stays as it is: in the Bring! app under',
    bringPath: 'Profile → More settings → Change password',
    bringOr: 'or at',
    bringPassword2:
      '. Then connect here with your Google account’s email address and the new password.',
    bringPrivacy:
      'The password is only sent to sign in to Bring! and isn’t stored. The shopping list is kept in step with Bring!. Bring! doesn’t offer an official interface for this – if Bring! changes something, the connection may stop working.',
    connected: (list: string, n: number) =>
      `Connected to Bring! – list “${list}”, ${n} ${n === 1 ? 'item' : 'items'} on it.`,
    whichList: 'Which list?',
    listChanged: 'List switched',
    synced: 'Updated',
    syncNow: 'Update now',
    disconnected: 'Bring! is disconnected – the Larovi list is back.',
    disconnect: 'Disconnect',
    bringEmail: 'Bring! email',
    bringPw: 'Bring! password',
    connecting: 'Connecting …',
    connect: 'Connect to Bring!',
  },
} as const;

export const settingsText = SETTINGS_TEXT[locale];
